// /security — security disclosure page. Points to the mailbox and
// the security.txt file (served from /.well-known/security.txt via the
// Vite public dir).
import { useSeo } from "@/hooks";
import { MarketingShell } from "./MarketingShell";

const DISCLOSURE_EMAIL = "neel@neels.dev";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-rule-row pt-6 first:border-t-0 first:pt-0">
      <h2 className="font-mono text-ink" style={{ fontSize: "clamp(20px, 2vw, 26px)", letterSpacing: "-0.02em" }}>{title}</h2>
      <div className="mt-4 max-w-[720px] space-y-3 text-ui text-ink-soft">{children}</div>
    </section>
  );
}

export function SecurityPage() {
  useSeo({
    title: "Security disclosure — malbox",
    description: "Found a vulnerability in malbox? Report it to neel@neels.dev. Scope, response SLAs, and safe-harbor policy for good-faith security research against malbox.neels.dev.",
    path: "/security",
    keywords: "malbox security, security disclosure, responsible disclosure, security.txt, malware sandbox security",
  });
  return (
    <MarketingShell
      eyebrow="Security"
      title="Report a vulnerability."
      lede={<>We're a malware sandbox. Isolation and least-privilege are the whole game. If you've found a bug that affects either, we want to hear it fast.</>}
    >
      <div className="space-y-10">
        <Section title="Contact">
          <p>
            Email <a href={`mailto:${DISCLOSURE_EMAIL}`} className="text-ink underline">{DISCLOSURE_EMAIL}</a> with a reproduction and your impact assessment.
          </p>
          <p>
            Machine-readable disclosure channel: <a href="/.well-known/security.txt" className="text-ink underline font-mono">/.well-known/security.txt</a>.
          </p>
        </Section>

        <Section title="Scope">
          <p className="text-ink">In scope:</p>
          <ul className="list-inside list-disc space-y-1 pl-2">
            <li>Guest-to-host escape (QEMU / libvirt / KVM paths we expose)</li>
            <li>Cross-tenant information disclosure (one submitter reading another's results)</li>
            <li>Server-side RCE in the control plane, backend, or any off-host worker</li>
            <li>Authentication / authorization bypass</li>
            <li>Secrets or credentials leaked in artifacts, logs, or responses</li>
            <li>Supply-chain in the Nix flake pin set</li>
          </ul>
          <p className="mt-4 text-ink">Out of scope:</p>
          <ul className="list-inside list-disc space-y-1 pl-2">
            <li>Issues in the sample itself (the whole point is to run malware)</li>
            <li>Rate-limit absence on anonymous endpoints during beta</li>
            <li>Third-party intel providers' responses (take those to the provider)</li>
            <li>Social engineering, physical attacks, DoS requiring network-level amplification</li>
          </ul>
        </Section>

        <Section title="Response">
          <p>
            Initial reply within 72 hours. Fix target depends on severity:
          </p>
          <ul className="list-inside list-disc space-y-1 pl-2">
            <li><b className="font-medium text-ink">Critical</b> (escape, cross-tenant, auth bypass): patched + deployed in &lt; 48 h</li>
            <li><b className="font-medium text-ink">High</b>: patched in &lt; 1 week</li>
            <li><b className="font-medium text-ink">Medium / Low</b>: patched in the next release</li>
          </ul>
          <p>
            Credit on the Changelog on request. No bounty program yet; we'll swap you a thank-you and real engagement.
          </p>
        </Section>

        <Section title="Safe harbor">
          <p>
            Good-faith testing against the hosted service (malbox.neels.dev) is welcome and will not result in legal action, provided you:
          </p>
          <ul className="list-inside list-disc space-y-1 pl-2">
            <li>Stop at proof-of-concept; don't exfiltrate real data</li>
            <li>Don't disrupt the service for other users</li>
            <li>Give us a reasonable window to fix before public disclosure (we suggest 90 days or a joint statement, whichever first)</li>
            <li>Don't attempt attacks that require social engineering or physical access</li>
          </ul>
        </Section>
      </div>
    </MarketingShell>
  );
}
